const PAGE_LABELS = new Set(["dashboard", "positions", "profile"]);

const ABSOLUTE_ORIGIN = /^(https?:\/\/[^/?#]+)/i;

const UI_OPAQUE =
  /(?:https?:\/\/[^/?#\s"'<>]+)?\/admin\/customer-view(?:\/([^/?#\s"'<>]*))?(?:\/([^/?#\s"'<>]*))?(?:[?#][^\s"'<>]*)?/g;

const UI_RAW =
  /(?:https?:\/\/[^/?#\s"'<>]+)?\/admin\/users\/([^/?#\s"'<>]+)\/customer-view(?:\/([^/?#\s"'<>]*))?(?:[?#][^\s"'<>]*)?/g;

type ApiFamily = {
  prefix: string;
  suffix?: string;
};

const API_FAMILIES: ApiFamily[] = [
  { prefix: "/v2/admin/users/" },
  { prefix: "/v2/admin/user-configurations/" },
  { prefix: "/v2/form/response/user/" },
  { prefix: "/v2/admin/user-accounts/", suffix: "/info" },
  { prefix: "/v2/admin/user-accounts/", suffix: "/summary" },
  { prefix: "/v2/admin/user-accounts/", suffix: "/positionsV2" },
  { prefix: "/v2/investor/strategy/" },
  { prefix: "/v3/investor/strategy/factors/" },
  { prefix: "/v3/investors/", suffix: "/actual-performance" },
  { prefix: "/v2/admin/user-contract-exclusions/" },
  { prefix: "/v3/investors/", suffix: "/subscriptions" },
];

class TelemetrySanitizeError extends Error {
  constructor() {
    super("Customer View telemetry sanitization failed");
    this.name = "TelemetrySanitizeError";
  }
}

function splitOrigin(value: string): { origin: string; remainder: string } {
  const match = value.match(ABSOLUTE_ORIGIN);
  if (!match) return { origin: "", remainder: value };
  return { origin: match[1], remainder: value.slice(match[1].length) };
}

function stripQueryAndFragment(pathAndRest: string): string {
  const cut = pathAndRest.search(/[?#]/);
  return cut === -1 ? pathAndRest : pathAndRest.slice(0, cut);
}

function recognizedPage(segment: string | undefined): string | null {
  if (!segment) return null;
  return PAGE_LABELS.has(segment) ? segment : "unknown";
}

function rewriteOpaquePath(path: string): string | null {
  if (path !== "/admin/customer-view" && !path.startsWith("/admin/customer-view/")) {
    return null;
  }
  const parts = path.split("/");
  // ["", "admin", "customer-view", key?, page?]
  if (parts.length < 3) return null;
  const key = parts[3];
  const page = parts[4];
  if (!key) return "/admin/customer-view";
  const pageLabel = recognizedPage(page);
  return pageLabel
    ? `/admin/customer-view/:REDACTED/${pageLabel}`
    : "/admin/customer-view/:REDACTED";
}

function rewriteRawPath(path: string): string | null {
  if (!path.startsWith("/admin/users/")) return null;
  const marker = "/customer-view";
  const usersPrefix = "/admin/users/";
  const afterUsers = path.slice(usersPrefix.length);
  const markerIndex = afterUsers.indexOf(marker);
  if (markerIndex <= 0) return null;
  const afterMarker = afterUsers.slice(markerIndex + marker.length);
  if (afterMarker && !afterMarker.startsWith("/")) return null;
  const page = afterMarker.startsWith("/") ? afterMarker.slice(1).split("/")[0] : "";
  const pageLabel = recognizedPage(page);
  return pageLabel
    ? `/admin/users/:REDACTED/customer-view/${pageLabel}`
    : "/admin/users/:REDACTED/customer-view";
}

function rewriteApiPath(path: string): string | null {
  for (const family of API_FAMILIES) {
    if (!path.startsWith(family.prefix)) continue;
    const rest = path.slice(family.prefix.length);
    if (!rest) continue;
    const slash = rest.indexOf("/");
    const target = slash === -1 ? rest : rest.slice(0, slash);
    const afterTarget = slash === -1 ? "" : rest.slice(slash);
    if (!target) continue;
    if (family.suffix) {
      if (afterTarget === family.suffix || afterTarget.startsWith(`${family.suffix}/`) || afterTarget.startsWith(`${family.suffix}?`)) {
        const suffixRest = afterTarget.slice(family.suffix.length);
        if (suffixRest && !suffixRest.startsWith("/") && !suffixRest.startsWith("?") && suffixRest !== "") {
          continue;
        }
        return `${family.prefix}:REDACTED${family.suffix}`;
      }
      continue;
    }
    if (slash === -1) {
      return `${family.prefix}:REDACTED`;
    }
    continue;
  }
  return null;
}

function rewriteSingleUrl(value: string): string | null {
  const { origin, remainder } = splitOrigin(value);
  const path = stripQueryAndFragment(remainder);
  const rewritten =
    rewriteOpaquePath(path)
    ?? rewriteRawPath(path)
    ?? rewriteApiPath(path);
  if (!rewritten) return null;
  return `${origin}${rewritten}`;
}

function replaceAll(value: string, regex: RegExp, replacer: (match: string) => string): string {
  regex.lastIndex = 0;
  return value.replace(regex, (match) => replacer(match));
}

export function sanitizeAdminCustomerViewTelemetryString(value: string): string {
  const whole = rewriteSingleUrl(value);
  if (whole !== null) return whole;

  let next = value;
  next = replaceAll(next, UI_RAW, (match) => {
    const rewritten = rewriteSingleUrl(match);
    if (rewritten === null) throw new TelemetrySanitizeError();
    return rewritten;
  });
  next = replaceAll(next, UI_OPAQUE, (match) => {
    if (match.includes("/admin/users/")) return match;
    const rewritten = rewriteSingleUrl(match);
    if (rewritten === null) throw new TelemetrySanitizeError();
    return rewritten;
  });

  for (const family of API_FAMILIES) {
    const escapedPrefix = family.prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const escapedSuffix = family.suffix
      ? family.suffix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      : "";
    const queryOrFragment = `(?:[?#][^\\s"'<>]*)?`;
    const pattern = family.suffix
      ? new RegExp(
        `(?:https?:\\/\\/[^/?#\\s"'<>]+)?${escapedPrefix}[^/?#\\s"'<>]+${escapedSuffix}${queryOrFragment}`,
        "g"
      )
      : new RegExp(
        `(?:https?:\\/\\/[^/?#\\s"'<>]+)?${escapedPrefix}[^/?#\\s"'<>]+${queryOrFragment}(?![^/?#\\s"'<>])`,
        "g"
      );
    next = replaceAll(next, pattern, (match) => {
      const rewritten = rewriteSingleUrl(match);
      if (rewritten === null) throw new TelemetrySanitizeError();
      return rewritten;
    });
  }

  return next;
}

const IDENTITY_KEYS = new Set([
  "distinct_id",
  "$distinct_id",
  "$user_id",
  "user_id",
]);

export function sanitizeAdminCustomerViewTelemetryValue<T>(value: T): T {
  const seen = new WeakMap<object, unknown>();

  const walk = (input: unknown): unknown => {
    if (typeof input === "string") {
      return sanitizeAdminCustomerViewTelemetryString(input);
    }
    if (input === null || typeof input !== "object") {
      return input;
    }
    const cached = seen.get(input);
    if (cached) return cached;
    if (Array.isArray(input)) {
      const copy: unknown[] = [];
      seen.set(input, copy);
      for (const item of input) {
        copy.push(walk(item));
      }
      return copy;
    }
    const proto = Object.getPrototypeOf(input);
    if (proto !== Object.prototype && proto !== null) {
      if (input instanceof Error) {
        const copy = new Error(sanitizeAdminCustomerViewTelemetryString(input.message));
        copy.name = input.name;
        if (typeof input.stack === "string") {
          copy.stack = sanitizeAdminCustomerViewTelemetryString(input.stack);
        }
        seen.set(input, copy);
        return copy;
      }
      return input;
    }
    const copy: Record<string, unknown> = {};
    seen.set(input, copy);
    for (const [key, nested] of Object.entries(input as Record<string, unknown>)) {
      copy[key] = IDENTITY_KEYS.has(key) ? nested : walk(nested);
    }
    return copy;
  };

  try {
    return walk(value) as T;
  } catch {
    throw new TelemetrySanitizeError();
  }
}

export function trySanitizeAdminCustomerViewTelemetryValue<T>(value: T): T | null {
  try {
    return sanitizeAdminCustomerViewTelemetryValue(value);
  } catch {
    return null;
  }
}
